import { reactive } from "vue"
import Mousetrap from "mousetrap"

const mousetrap = Mousetrap()

export interface Shortcut {
  // One combo ("command+k", "g i") or several that share a handler.
  keys: string | string[]
  fn: (e: Event, combo?: string) => void
  // Human-readable label + group for the "?" overlay. Omit label to keep a
  // binding functional but hidden from the overlay.
  label?: string
  group?: string
  // The browser default for the combo is prevented before fn runs; set false
  // for the rare shortcut that wants the default to go through.
  preventDefault?: boolean
}

export interface RegisteredShortcut {
  label: string
  group: string
}

// Reactive registry of the currently-active shortcuts, keyed by combo string.
// addShortcuts() adds entries and removeShortcuts() prunes them, so — because
// views add on mount and remove on unmount — this always reflects the
// shortcuts live for wherever the user is right now. That's what the "?"
// overlay reads.
export const shortcutRegistry = reactive(new Map<string, RegisteredShortcut>())

function asList(shortcuts: Shortcut | Shortcut[]): Shortcut[] {
  return Array.isArray(shortcuts) ? shortcuts : [shortcuts]
}

function combos(keys: string | string[]): string[] {
  return Array.isArray(keys) ? keys : [keys]
}

// Thin wrapper over the single shared Mousetrap instance. Pass one shortcut or
// a list; define the list once in a view and hand the same array to
// addShortcuts() on mount and removeShortcuts() on unmount.
const wrapped = {
  addShortcuts(shortcuts: Shortcut | Shortcut[]) {
    for (const s of asList(shortcuts)) {
      const handler = (e: Event, combo?: string) => {
        if (s.preventDefault !== false) e.preventDefault()
        return s.fn(e, combo)
      }
      for (const combo of combos(s.keys)) {
        mousetrap.bind(combo, handler)
        if (s.label) shortcutRegistry.set(combo, { label: s.label, group: s.group ?? "General" })
      }
    }
  },
  removeShortcuts(shortcuts: Shortcut | Shortcut[]) {
    for (const s of asList(shortcuts)) {
      for (const combo of combos(s.keys)) {
        mousetrap.unbind(combo)
        shortcutRegistry.delete(combo)
      }
    }
  },
  trigger(keys: string, action?: string) {
    return mousetrap.trigger(keys, action)
  },
  reset() {
    mousetrap.reset()
    shortcutRegistry.clear()
  },
}

export function useMousetrap() {
  return wrapped
}
